import React from 'react'

import { Icon, T } from 'src/govwell-ui'
import { useIsInternalStaffAIAnalyzeEnabled } from 'src/utils/featureFlagging/featureFlagging'

type CapabilityCardProps = {
  icon: React.ReactNode
  title: string
  description: string
  example: string
  badgeColor: string
  badgeBgColor: string
}

const CapabilityCard = ({
  icon,
  title,
  description,
  example,
  badgeColor,
  badgeBgColor,
}: CapabilityCardProps) => (
  <div className="flex flex-1 flex-col rounded-xl border border-neutral-200 bg-white p-5 transition-shadow hover:shadow-md">
    <div
      className={`mb-3 flex w-fit items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-medium ${badgeBgColor} ${badgeColor}`}
    >
      {icon}
      <span>{title}</span>
    </div>
    <T.Body className="mb-1 font-semibold text-neutral-900">{title}</T.Body>
    <T.BodySmall className="mb-3 text-neutral-600">{description}</T.BodySmall>
    <T.BodySmall className="text-neutral-500 italic">&ldquo;{example}&rdquo;</T.BodySmall>
  </div>
)

const StaffAIChatEmptyState = () => {
  const isAnalyzeEnabled = useIsInternalStaffAIAnalyzeEnabled()

  return (
    <div className="flex flex-1 flex-col items-center justify-center px-8 py-12">
      {/* Hero Icon */}
      <div className="mb-6 flex h-20 w-20 items-center justify-center rounded-full bg-neutral-900">
        <Icon.FaSparkles className="h-10 w-10 text-white" />
      </div>

      {/* Title & Description */}
      <div className="mb-2 flex items-center justify-center gap-2">
        <T.H2 className="text-center text-neutral-900">GovWell AI Assistant</T.H2>
        <span className="rounded-full bg-amber-100 px-2 py-0.5 text-xs font-medium text-amber-700">
          Beta
        </span>
      </div>
      <T.Body className="mb-10 max-w-lg text-center text-neutral-600">
        {isAnalyzeEnabled
          ? 'Your AI-powered assistant for government workflows. Ask questions and analyze data—all in one place.'
          : 'Your AI-powered assistant for government workflows. Get answers about regulations, procedures, and best practices.'}
      </T.Body>

      {/* Capability Cards */}
      <div className="flex w-full max-w-xl gap-4">
        <CapabilityCard
          icon={<Icon.FaCircleQuestion className="h-3 w-3" />}
          title="Ask"
          description="General knowledge about regulations, codes, and procedures"
          example="What's the difference between a variance and a special exception?"
          badgeColor="text-blue-700"
          badgeBgColor="bg-blue-100"
        />
        {isAnalyzeEnabled && (
          <CapabilityCard
            icon={<Icon.FaChartLine className="h-3 w-3" />}
            title="Analyze"
            description="Query your GovWell data for insights and reports"
            example="Which inspector completed the most inspections last month?"
            badgeColor="text-purple-700"
            badgeBgColor="bg-purple-100"
          />
        )}
      </div>
    </div>
  )
}

export default React.memo(StaffAIChatEmptyState)
